import React, { useState, useEffect } from 'react';

/**
 * ENGINEER'S BETTING FLOW
 *
 * PURPOSE: Show EXACTLY what happens when placing a bet
 * SUCCESS: All 6 steps complete with real data
 *
 * CURRENT PROBLEM: Step 2 waits for selectedOption but the check happens
 * before React re-renders with the new state
 *
 * DATA FLOW:
 * 1. User clicks button → setSelectedOption('YES')
 * 2. React schedules re-render (HASN'T HAPPENED YET)
 * 3. Step checker runs → selectedOption still null
 * 4. React re-renders → selectedOption now 'YES' (TOO LATE)
 */

export default function EngineerBettingFlow() {
  // STATE
  const [selectedOption, setSelectedOption] = useState<string | null>(null);
  const [betAmount, setBetAmount] = useState<string>('');
  const [isRunning, setIsRunning] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [logs, setLogs] = useState<string[]>([]);
  const [stepResults, setStepResults] = useState<any[]>([]);

  // LOG EVERYTHING
  const log = (message: string, data?: any) => {
    const timestamp = new Date().toISOString().split('T')[1].slice(0, -1);
    const logEntry = `[${timestamp}] ${message}`;
    console.log(logEntry, data || '');
    setLogs(prev => [...prev, data ? `${logEntry} ${JSON.stringify(data)}` : logEntry]);
  };

  // STEP DEFINITIONS
  const steps = [
    {
      name: 'INIT',
      check: () => true,
      execute: () => {
        log('Step 1: Initialization');
        log('Current state:', { selectedOption, betAmount });
        return { success: true, data: 'initialized' };
      }
    },
    {
      name: 'WAIT_FOR_OPTION',
      check: () => {
        const hasOption = selectedOption !== null;
        log(`Step 2 Check: selectedOption=${selectedOption} hasOption=${hasOption}`);
        return hasOption;
      },
      execute: () => {
        log('Step 2: Option selected', { selectedOption });
        return { success: true, data: selectedOption };
      }
    },
    {
      name: 'WAIT_FOR_AMOUNT',
      check: () => {
        const hasAmount = betAmount !== '' && parseFloat(betAmount) > 0;
        log(`Step 3 Check: betAmount=${betAmount} hasAmount=${hasAmount}`);
        return hasAmount;
      },
      execute: () => {
        log('Step 3: Amount entered', { betAmount });
        return { success: true, data: parseFloat(betAmount) };
      }
    },
    {
      name: 'CALCULATE',
      check: () => true,
      execute: () => {
        const amount = parseFloat(betAmount);
        const prob = selectedOption === 'YES' ? 0.75 : 0.25;
        const newProb = (prob * 100 + amount) / (100 + amount);
        const result = {
          amount,
          option: selectedOption,
          oldProb: prob,
          newProb,
          change: ((newProb - prob) * 100).toFixed(2) + '%'
        };
        log('Step 4: Calculated', result);
        return { success: true, data: result };
      }
    },
    {
      name: 'SUBMIT',
      check: () => true,
      execute: () => {
        const betId = `bet-${Date.now()}`;
        log('Step 5: Submitting bet', { betId });
        // REAL API CALL WOULD GO HERE
        return { success: true, data: { betId, status: 'submitted' } };
      }
    },
    {
      name: 'COMPLETE',
      check: () => true,
      execute: () => {
        log('Step 6: Complete');
        return { success: true, data: 'done' };
      }
    }
  ];

  // EXECUTION ENGINE
  useEffect(() => {
    if (!isRunning) return;

    const interval = setInterval(() => {
      if (currentStep >= steps.length) {
        log('FLOW COMPLETE');
        setIsRunning(false);
        return;
      }

      const step = steps[currentStep];
      log(`\n=== EXECUTING STEP ${currentStep + 1}: ${step.name} ===`);

      // CHECK IF STEP CAN RUN
      if (step.check()) {
        const result = step.execute();
        setStepResults(prev => [...prev, result]);
        setCurrentStep(prev => prev + 1);
      } else {
        log(`WAITING: Step ${currentStep + 1} conditions not met`);
      }
    }, 500);

    return () => clearInterval(interval);
  }, [isRunning, currentStep, selectedOption, betAmount]); // DEPENDENCIES MATTER

  // UI HANDLERS
  const handleStart = () => {
    log('\n========== STARTING FLOW ==========');
    setCurrentStep(0);
    setStepResults([]);
    setIsRunning(true);
  };

  const handleOptionSelect = (option: string) => {
    log(`USER ACTION: Selected ${option}`);
    log('State BEFORE setState:', { selectedOption });
    setSelectedOption(option);
    log('setState called, but state NOT updated yet (async)');
    // THE PROBLEM: selectedOption is STILL null here!
  };

  const handleAmountChange = (amount: string) => {
    log(`USER ACTION: Entered amount ${amount}`);
    setBetAmount(amount);
  };

  return (
    <div className="min-h-screen bg-black text-green-400 font-mono p-4">
      <div className="max-w-7xl mx-auto">
        <h1 className="text-2xl mb-4">ENGINEER'S BETTING FLOW DEBUGGER</h1>

        <div className="grid grid-cols-3 gap-4">
          {/* COLUMN 1: CONTROLS */}
          <div className="border border-green-400 p-4">
            <h2 className="text-xl mb-4">CONTROLS</h2>

            <button
              onClick={handleStart}
              className="w-full bg-green-400 text-black p-2 mb-4 hover:bg-green-300"
              disabled={isRunning}
            >
              {isRunning ? `RUNNING STEP ${currentStep + 1}...` : 'START FLOW'}
            </button>

            <div className="mb-4">
              <label className="block mb-2">OPTION SELECTOR:</label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={() => handleOptionSelect('YES')}
                  className={`p-2 border ${
                    selectedOption === 'YES' ? 'bg-green-400 text-black' : 'border-green-400'
                  }`}
                >
                  YES
                </button>
                <button
                  onClick={() => handleOptionSelect('NO')}
                  className={`p-2 border ${
                    selectedOption === 'NO' ? 'bg-red-400 text-black' : 'border-green-400'
                  }`}
                >
                  NO
                </button>
              </div>
            </div>

            <div className="mb-4">
              <label className="block mb-2">AMOUNT INPUT:</label>
              <input
                type="number"
                value={betAmount}
                onChange={(e) => handleAmountChange(e.target.value)}
                className="w-full bg-black border border-green-400 p-2 text-green-400"
                placeholder="Enter amount"
              />
            </div>

            <div className="border-t border-green-400 pt-4">
              <h3 className="mb-2">CURRENT STATE:</h3>
              <pre className="text-xs">
{JSON.stringify({
  selectedOption,
  betAmount,
  isRunning,
  currentStep
}, null, 2)}
              </pre>
            </div>
          </div>

          {/* COLUMN 2: STEP STATUS */}
          <div className="border border-green-400 p-4">
            <h2 className="text-xl mb-4">STEP STATUS</h2>

            {steps.map((step, index) => (
              <div
                key={index}
                className={`mb-2 p-2 border ${
                  index < currentStep
                    ? 'border-green-400 bg-green-900'
                    : index === currentStep
                    ? 'border-yellow-400 bg-yellow-900'
                    : 'border-gray-600'
                }`}
              >
                <div className="flex justify-between">
                  <span>Step {index + 1}: {step.name}</span>
                  <span>
                    {index < currentStep ? '✓' : index === currentStep ? '⏳' : '○'}
                  </span>
                </div>
                {stepResults[index] && (
                  <pre className="text-xs mt-1">
{JSON.stringify(stepResults[index].data, null, 2)}
                  </pre>
                )}
              </div>
            ))}

            <div className="mt-4 p-2 border border-red-400">
              <h3 className="text-red-400">THE PROBLEM:</h3>
              <p className="text-xs">
                When you click YES, setSelectedOption runs but React doesn't
                re-render immediately. The step checker runs before the new
                state is available, so it still sees null.
              </p>
            </div>
          </div>

          {/* COLUMN 3: LOGS */}
          <div className="border border-green-400 p-4">
            <h2 className="text-xl mb-4">EXECUTION LOG</h2>
            <div className="h-[600px] overflow-y-auto text-xs">
              {logs.map((log, i) => (
                <div
                  key={i}
                  className={
                    log.includes('ERROR') ? 'text-red-400' :
                    log.includes('SUCCESS') ? 'text-green-400' :
                    log.includes('WAITING') ? 'text-yellow-400' :
                    'text-gray-400'
                  }
                >
                  {log}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* BOTTOM: SOLUTION */}
        <div className="mt-4 border border-blue-400 p-4">
          <h2 className="text-xl text-blue-400 mb-2">THE FIX:</h2>
          <pre className="text-xs">
{`// PROBLEM: Checking state directly
if (selectedOption !== null) { /* Still null! */ }

// SOLUTION 1: Use refs for immediate values
const optionRef = useRef(null);
const handleSelect = (opt) => {
  optionRef.current = opt; // Immediate
  setSelectedOption(opt); // Async
}

// SOLUTION 2: Pass state to step checker
const checkStep = (currentState) => {
  return currentState.selectedOption !== null;
}

// SOLUTION 3: Use state machine
const [machine, send] = useMachine({
  WAITING: { on: { SELECT: 'SELECTED' }},
  SELECTED: { on: { AMOUNT: 'READY' }}
})`}
          </pre>
        </div>
      </div>
    </div>
  );
}